import { apiClient } from '../ruutsApi/client'
import endpoints from '../ruutsApi/endpoints'
import { errors } from '../ruutsApi/errors'
import { unwrapCollection, unwrapNullableSingle } from '../ruutsApi/normalize'
import { messages } from '../../utils/messages/index'

export const allHubsId = 'all'

// The /hubId claim can be a single value or an array that mixes concrete hubs
// with the all-hubs sentinel (e.g. ['hub-1', 'all']); when 'all' is present it
// prevails and grants access to every hub.
export type HubIdClaim = string | string[]
export type User = { '/hubId'?: HubIdClaim }

const grantsAllHubs = (hubId: HubIdClaim): boolean =>
  hubId === allHubsId || (Array.isArray(hubId) && hubId.includes(allHubsId))

type RawFarm = {
  id: string
  name: string
  geometry: object | null
  error?: { key: string }
  [key: string]: unknown
}

async function getFarmsByEndpoint<T = RawFarm>({
  id,
  user,
  endpoint,
  signal,
}: {
  id?: string
  user: User
  endpoint: string
  signal?: AbortSignal
}): Promise<T[]> {
  const params: Record<string, HubIdClaim> = {}
  const hubId = user['/hubId']

  if (!hubId || (Array.isArray(hubId) && hubId.length === 0)) {
    throw new errors.BusinessEntityError({
      response: user,
      message: 'No se encontró el hubId del usuario',
      needSupport: false,
    })
  }

  // Users with all-hubs access must see every farm regardless of hub; scoped users only see their hub's farms
  if (!grantsAllHubs(hubId)) {
    params.hubId = hubId
  }

  const endpointPath = id ? `${endpoint}/${id}` : endpoint
  const response = await apiClient.get(endpointPath, { params, signal })
  return unwrapCollection<T>(response)
}

export async function getFarm({
  id,
  user,
  signal,
}: {
  id: string
  user: User
  signal?: AbortSignal
}) {
  const farms = await getFarmsByEndpoint({ id, user, endpoint: endpoints.farms, signal })

  if (!farms || !farms.length) {
    throw new errors.BusinessEntityError({
      response: farms,
      message: `No se encontró establecimiento con el id ${id}`,
      needSupport: false,
    })
  }

  if (farms.length !== 1) {
    throw new errors.BusinessEntityError({
      response: farms,
      message: `Error de configuración, se encontró mas de un negocio relacionado para el establecimiento ${farms[0].name}`,
      needSupport: false,
    })
  }

  const rawFarm = farms[0]
  const farm = {
    ...rawFarm,
    valid: rawFarm?.error === undefined,
    hasPerimeter: rawFarm.geometry != null,
  }

  if (!farm.valid) {
    const keyError = farm.error!.key
    const messageKey = messages.keys[keyError as keyof typeof messages.keys]
    throw new errors.BusinessEntityError({
      response: farm,
      message: messages.getMessage(messageKey ?? messages.keys.FILL_FORM_SUPPORT_CONTACT_BUTTON),
      needSupport: false,
    })
  }

  return farm
}

export async function getFarmProjectArea({
  farmId,
  signal,
}: {
  farmId: string
  signal?: AbortSignal
}): Promise<GeoJSON.Feature | null> {
  const response = await apiClient.get(`${endpoints.farms}/${farmId}/projectArea`, { signal })
  return unwrapNullableSingle<GeoJSON.Feature>(response)
}
